import { useEffect, useMemo, useState, } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCollaboratorsList, getExclusions, getIsLoading, getModuleList, getResources, getTree } from "../../selectors";
import {
    createModuleExclusionAction,
    deleteModuleExclusionAction,
    duplicateModuleExclusionAction,
    getModulesExclusionsAction,
    updateModuleExclusionAction,
} from "../../actions";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getLocalizedString } from "@icarius-localization/strings";
import { RESET_ALL } from "../../actionTypes";
import { openDialogAction } from "@icarius-common/dialog/actions";
import { roleNames } from "@icarius-utils/constants";

const useExclusions = () => {

    const [selectedItem, setSelectedItem] = useState(null);
    const [formIsOpen, setFormIsOpen] = useState(false);
    const [duplicateFormIsOpen, setDuplicateFormIsOpen] = useState(false);

    const dispatch = useDispatch();
    const isLoading = useSelector(getIsLoading);
    const exclusions = useSelector(getExclusions);
    const moduleList = useSelector(getModuleList);
    const tree = useSelector(getTree);
    const collaboratorsList = useSelector(getCollaboratorsList);
    const resources = useSelector(getResources);

    const formattedExclusions = useMemo(() => {
        if (!exclusions?.length) return [];
        return exclusions.map((exclusion) => {
            return ({
                ...exclusion,
                roleString: getLocalizedString(roleNames[exclusion.role])
            })
        })
    }, [exclusions])

    useEffect(() => {
        dispatch(getModulesExclusionsAction());

        return () => {
            dispatch({ type: RESET_ALL });
        }
    }, [dispatch])

    const handleOpenCreateForm = () => {
        setFormIsOpen(true);
    }

    const handleOpenUpdateForm = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        setSelectedItem(selectedRows[0]);
        setFormIsOpen(true);
    }

    const handleCloseForm = () => {
        setSelectedItem(null);
        setFormIsOpen(false);
    }

    const handleCreate = (dataToSend) => {
        dispatch(createModuleExclusionAction(dataToSend))
            .then((resp) => {
                if (resp?.status === 200) {
                    handleCloseForm();
                }
            });
    }

    const handleUpdate = (dataToSend) => {
        dispatch(updateModuleExclusionAction(selectedItem.id, dataToSend))
            .then((resp) => {
                if (resp?.status === 200) {
                    handleCloseForm();
                }
            });
    }

    const handleOpenDeleteDialog = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        dispatch(openDialogAction({
            title: getLocalizedString("atention"),
            msg: getLocalizedString("deleteExclusionQuestion"),
            onConfirm: () => {
                dispatch(deleteModuleExclusionAction(selectedRows[0].id))
                    .then((resp) => {
                        if (resp?.status === 200) {
                            handleCloseForm();
                        }
                    });
            }
        }))
    }

    const handleOpenDuplicateForm = (gridRef) => {
        let selectedRows = gridRef.api.getSelectedRows();
        if (selectedRows.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        setSelectedItem(selectedRows[0]);
        setDuplicateFormIsOpen(true);
    }

    const handleCloseDuplicateForm = () => {
        setSelectedItem(null);
        setDuplicateFormIsOpen(false);
    }

    const handleDuplicate = (dataToSend) => {
        dispatch(duplicateModuleExclusionAction(selectedItem.id, dataToSend))
            .then((resp) => {
                if (resp?.status === 200) {
                    handleCloseDuplicateForm();
                }
            });
    }

    const state = {
        selectedItem,
        formIsOpen,
        duplicateFormIsOpen,
    }

    const handlers = {
        handleOpenCreateForm,
        handleOpenUpdateForm,
        handleCloseForm,
        handleOpenDeleteDialog,
        handleOpenDuplicateForm,
        handleCloseDuplicateForm,
        handleCreate,
        handleUpdate,
        handleDuplicate,
    }

    return {
        isLoading,
        exclusions: formattedExclusions,
        moduleList,
        tree,
        collaboratorsList,
        resources,
        state,
        handlers,
    }
}

export default useExclusions;